import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  SquarePlus,
  ExternalLink,
  Copy,
  RotateCw,
  X,
  ArrowLeft,
  ArrowRight,
  Printer,
  Camera,
  Maximize2,
  Minimize2,
  MousePointerClick,
  Filter,
  FilterX,
  ChevronRight,
  Lock,
  LockOpen,
  FileSpreadsheet,
} from "lucide-react";
import { toast } from "sonner";
import { useTabs } from "../../contexts/TabsContext";
import { useAuth } from "../../contexts/AuthContext";
import { getTitleForHref } from "../../config/navigation";
import {
  describeRow,
  describeColumn,
  describeColumns,
  clearAll,
  getActive,
  getPicks,
  subscribe,
  togglePick,
  TABLE_SELECTOR,
  selectionExport,
  tableExport,
  exportRowCount,
} from "../../lib/quickFilter";
import RowFilterFlyout from "./RowFilterFlyout";
import PushToDocsDialog from "../docs/PushToDocsDialog";
import { collectContextActions } from "../../lib/contextActions";

// Custom right-click menu for the ERP shell. Normal right-click opens THIS menu
// with only the actions a web page can genuinely perform; Ctrl/Cmd/Shift +
// right-click (and right-clicking editable fields or a live text selection)
// falls through to the real browser menu. Desktop only — the tab paradigm is.
const CLOSED = { open: false, x: 0, y: 0, href: null, extHref: null, filter: null, tabId: null, docs: null };

// The push-table endpoint's limits (routes/docs.js normalizeTablePayload).
const DOC_MAX_ROWS = 1000;
const DOC_MAX_COLS = 30;
const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

const ErpContextMenu = () => {
  const { openInNewTab, activateTab, reloadTab, closeOthers, toggleTabLock, tabs, activeId } = useTabs();
  const { token } = useAuth();
  const [menu, setMenu] = useState(CLOSED);
  // "Create Doc" payload ({ columns, rows, defaultName }) — its dialog
  // outlives the menu, which closes the moment the item is clicked.
  const [docPush, setDocPush] = useState(null);
  const [subOpen, setSubOpen] = useState(false); // Filter flyout open?
  // Live view of the filter store so the flyout re-renders as filters are
  // applied and as Alt+right-click adds cells to the selection.
  const [active, setActive] = useState(getActive);
  useEffect(() => subscribe(setActive), []);
  const ref = useRef(null);
  const filterBtnRef = useRef(null); // anchor for the Filter flyout position

  const close = useCallback(() => { setMenu(CLOSED); setSubOpen(false); }, []);

  useEffect(() => {
    const onContext = (e) => {
      if (window.innerWidth < 1024) return; // desktop-only feature
      if (e.ctrlKey || e.metaKey || e.shiftKey) return; // → real browser menu
      const t = e.target;
      if (!(t instanceof Element)) return;
      // Alt + right-click builds a multi-cell selection instead of opening the
      // menu, so you can pick several values and then act on them (Math /
      // Compare tabs of the Filter flyout).
      if (e.altKey) {
        if (togglePick(t)) { e.preventDefault(); return; }
        return;
      }
      // Keep the browser menu inside our own menu, on editable fields (so
      // copy/paste/spellcheck work), and when text is selected (copy).
      if (ref.current && ref.current.contains(t)) { e.preventDefault(); return; }
      if (t.closest('input, textarea, select, [contenteditable=""], [contenteditable="true"]')) return;
      // Embedded widgets with their own right-click menu (Univer's sheet
      // canvas — copy/paste/insert/freeze/…) opt out the same way: let the
      // real browser contextmenu event through to them undisturbed instead
      // of stacking our menu on top of theirs.
      if (t.closest("[data-erp-context-menu-exempt]")) return;
      const sel = window.getSelection && String(window.getSelection() || "");
      if (sel && sel.trim()) return;

      e.preventDefault();
      const anchor = t.closest("a[href]");
      const raw = anchor?.getAttribute("href") || null;
      const href = raw && raw.startsWith("/") ? raw : null; // internal ERP route
      const extHref = raw && /^https?:\/\//i.test(raw) ? raw : null;
      // One flyout, two entry points: a body cell describes its row, a column
      // header describes that column. Right-clicking a header opens the flyout
      // straight away — filtering is the only reason to right-click a title.
      const th = t.closest("th");
      let filter = null;
      if (th && th.closest("thead")) {
        const column = describeColumn(th);
        if (column) {
          filter = { table: column.table, columns: describeColumns(column.table), source: "header", column };
        }
      } else {
        const row = describeRow(t);
        if (row) filter = { ...row, source: "row" };
      }
      const tabId = t.closest("[data-tab-id]")?.getAttribute("data-tab-id") || null; // right-clicked a tab?
      setSubOpen(filter?.source === "header");
      const actions = collectContextActions(t); // page-registered items (lib/contextActions.js)
      // Create Doc: an Alt-pick selection wins wherever you right-click;
      // otherwise right-clicking a table offers its visible rows.
      const pickCount = getPicks().items.length;
      let docs = null;
      if (pickCount) docs = { kind: "selection", hint: plural(pickCount, "cell") };
      else {
        const table = t.closest(TABLE_SELECTOR);
        const n = table?.tHead ? exportRowCount(table) : 0;
        if (n) docs = { kind: "table", table, hint: plural(n, "row") };
      }
      setMenu({ open: true, x: e.clientX, y: e.clientY, href, extHref, filter, tabId, actions, docs });
    };
    document.addEventListener("contextmenu", onContext);
    return () => document.removeEventListener("contextmenu", onContext);
  }, []);

  // Dismiss on outside interaction.
  useEffect(() => {
    if (!menu.open) return;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) close(); };
    // Capture-phase + stopPropagation so ESC dismisses the menu WITHOUT also
    // reaching the QuickFilterBar's ESC-to-clear handler (bubble phase).
    const onKey = (e) => { if (e.key === "Escape") { e.stopPropagation(); close(); } };
    // Scrolling the PAGE moves the rows the menu is anchored to, so it closes.
    // Scrolling a list INSIDE the menu/flyout must not — that is normal use.
    const onScroll = (e) => {
      if (ref.current && e.target instanceof Node && ref.current.contains(e.target)) return;
      close();
    };
    // A wheel over a part of the menu that can't scroll would otherwise chain to
    // the page behind, which scrolls and closes the menu under the cursor. Eat
    // the event unless something inside the menu can actually consume it.
    const onWheel = (e) => {
      const el = ref.current;
      if (!el || !(e.target instanceof Node) || !el.contains(e.target)) return;
      for (let n = e.target instanceof Element ? e.target : e.target.parentElement; n && el.contains(n); n = n.parentElement) {
        const overflowY = getComputedStyle(n).overflowY;
        if ((overflowY === "auto" || overflowY === "scroll") && n.scrollHeight > n.clientHeight + 1) return;
      }
      e.preventDefault();
    };
    window.addEventListener("mousedown", onDown, true);
    window.addEventListener("keydown", onKey, true);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("wheel", onWheel, { capture: true, passive: false });
    window.addEventListener("resize", close);
    window.addEventListener("blur", close);
    return () => {
      window.removeEventListener("mousedown", onDown, true);
      window.removeEventListener("keydown", onKey, true);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("wheel", onWheel, { capture: true });
      window.removeEventListener("resize", close);
      window.removeEventListener("blur", close);
    };
  }, [menu.open, close]);

  // Close first (so the menu never appears in a screenshot / print), then act.
  const run = (fn, delay = 0) => () => {
    close();
    if (delay) setTimeout(fn, delay);
    else Promise.resolve().then(fn);
  };

  const toggleFullscreen = () => {
    if (document.fullscreenElement) document.exitFullscreen?.();
    else document.documentElement.requestFullscreen?.().catch(() => {});
  };

  const screenshot = async () => {
    try {
      const { default: html2canvas } = await import("html2canvas");
      const canvas = await html2canvas(document.body, {
        backgroundColor: getComputedStyle(document.body).backgroundColor || null,
        x: window.scrollX,
        y: window.scrollY,
        width: window.innerWidth,
        height: window.innerHeight,
        scale: window.devicePixelRatio || 1,
        useCORS: true,
        logging: false,
      });
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
      const a = document.createElement("a");
      a.href = canvas.toDataURL("image/png");
      a.download = `bana-erp-${stamp}.png`;
      a.click();
    } catch (err) {
      console.error("Screenshot failed:", err);
    }
  };

  // Build the sheet from what's on screen NOW (the menu just closed; nothing
  // re-rendered in between) and hand it to the shared Create Docs dialog.
  const createDoc = () => {
    const data = menu.docs?.kind === "selection" ? selectionExport() : tableExport(menu.docs?.table);
    if (!data || !data.rows.length) { toast.error("Nothing to put in a doc — no visible rows selected"); return; }
    if (data.rows.length > DOC_MAX_ROWS) {
      toast.error(`${data.rows.length} rows is too many for one sheet (max ${DOC_MAX_ROWS}) — filter or select fewer`);
      return;
    }
    if (data.columns.length > DOC_MAX_COLS) {
      toast.error(`${data.columns.length} columns is too many for one sheet (max ${DOC_MAX_COLS}) — select fewer`);
      return;
    }
    const page = tabs.find((x) => x.id === activeId)?.title || getTitleForHref(window.location.pathname);
    const d = new Date();
    const p2 = (n) => String(n).padStart(2, "0");
    setDocPush({ ...data, defaultName: `${page} ${p2(d.getDate())}-${p2(d.getMonth() + 1)}-${d.getFullYear()}` });
  };

  // Land on the pushed doc as an ERP tab — the one already open if there is.
  const openDoc = (href) => {
    const existing = tabs.find((x) => (x.href || "").split("?")[0] === href);
    if (existing) activateTab(existing.id);
    else openInNewTab(href);
  };

  const docDialog = docPush && (
    <PushToDocsDialog
      open
      onOpenChange={(o) => { if (!o) setDocPush(null); }}
      token={token}
      title="Create Doc"
      defaultName={docPush.defaultName}
      columns={docPush.columns}
      rows={docPush.rows}
      onOpenDoc={openDoc}
    />
  );

  if (!menu.open) return docDialog || null;

  const isFull = !!document.fullscreenElement;

  // Keep the menu inside the viewport, nudged off the exact click point so the
  // row/column you right-clicked stays readable instead of sitting under the
  // panel. The downward shift is the important one — it drops the menu (and the
  // flyout anchored to it) below the clicked row so that whole row stays visible.
  const W = 250;
  const H = 430 + (menu.docs ? 41 : 0);
  const OFF_X = 14;
  const OFF_Y = 18;
  // If the page shows a floating action bar pinned to the bottom (e.g. the
  // multi-select bar on Monthly Planning, tagged data-erp-floating-bar), keep
  // the menu clear of it so the two never overlap — clamp the menu's bottom to
  // just above the bar instead of to the viewport edge.
  const floatingBar = document.querySelector("[data-erp-floating-bar]");
  const bottomLimit = floatingBar
    ? Math.min(window.innerHeight, floatingBar.getBoundingClientRect().top) - 8
    : window.innerHeight - 8;
  const left = Math.max(8, Math.min(menu.x + OFF_X, window.innerWidth - W - 8));
  const top = Math.max(8, Math.min(menu.y + OFF_Y, bottomLimit - H));

  const Item = ({ icon: Icon, children, onClick, shortcut }) => (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className="w-full flex items-center gap-2.5 px-2.5 h-8 rounded-md text-sm text-foreground hover:bg-muted transition-colors text-left"
    >
      <Icon className="w-4 h-4 text-muted-foreground flex-shrink-0" />
      <span className="flex-1 truncate">{children}</span>
      {shortcut && (
        <span className="pl-3 text-[11px] text-muted-foreground/80 tabular-nums flex-shrink-0">{shortcut}</span>
      )}
    </button>
  );
  const Sep = () => <div className="my-1 h-px bg-border" />;

  // Lock/unlock the right-clicked tab (or the active one if the click missed a
  // tab). A locked tab can't be closed until unlocked.
  const lockTarget = tabs.find((t) => t.id === (menu.tabId || activeId));
  const isLocked = !!lockTarget?.locked;

  const panel = (
    <div
      ref={ref}
      role="menu"
      style={{ left, top }}
      className="fixed z-[300] w-[250px] p-1 rounded-lg border border-border bg-popover text-popover-foreground shadow-xl animate-in fade-in zoom-in-95 duration-100"
    >
      {/* Quick Filter — right-click a table cell; hovering "Filter" opens a
          side flyout listing every filterable column of that row (see the
          flyout render + lib/quickFilter.js). */}
      {menu.filter && (
        <>
          <button
            ref={filterBtnRef}
            type="button"
            role="menuitem"
            onMouseEnter={() => setSubOpen(true)}
            onClick={() => setSubOpen(true)}
            className={`w-full flex items-center gap-2.5 px-2.5 h-8 rounded-md text-sm text-foreground hover:bg-muted transition-colors text-left ${subOpen ? "bg-muted" : ""}`}
          >
            <Filter className="w-4 h-4 text-muted-foreground flex-shrink-0" />
            <span className="flex-1 truncate">Filter</span>
            <ChevronRight className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
          </button>
          <Sep />
        </>
      )}
      {!menu.filter && active.active && (
        <>
          <Item icon={FilterX} onClick={run(() => clearAll())}>
            Clear {active.filters.length > 1 ? "all filters" : "filter"}
          </Item>
          <Sep />
        </>
      )}

      {/* Everything below the Filter row closes the flyout on hover, mirroring
          how a native context menu collapses a submenu when you move away. */}
      <div onMouseEnter={() => setSubOpen(false)}>
        {/* New doc / new sheet in an existing doc, from the selection or
            the right-clicked table (see createDoc + PushToDocsDialog). */}
        {menu.docs && (
          <>
            <Item icon={FileSpreadsheet} onClick={run(createDoc)} shortcut={menu.docs.hint}>Create Doc</Item>
            <Sep />
          </>
        )}
        {menu.actions?.length > 0 && (
          <>
            {menu.actions.map((a) => (
              <div key={a.key}>
                <Item icon={a.icon} onClick={run(a.onClick)}>{a.label}</Item>
                {/* Optional colour row: { swatches: [{value,label}], swatch, onSwatch } */}
                {a.swatches && (
                  <div className="flex items-center gap-1.5 pl-9 pr-2.5 pb-1.5 pt-0.5">
                    {a.swatches.map((c) => (
                      <button
                        key={c.value || "default"}
                        type="button"
                        title={c.label}
                        aria-label={c.label}
                        onClick={run(() => a.onSwatch(c.value))}
                        style={c.value ? { backgroundColor: c.value } : undefined}
                        className={`w-4 h-4 rounded-full border transition-transform hover:scale-125 ${c.value ? "border-black/10" : "border-border bg-foreground"} ${a.swatch === c.value ? "ring-2 ring-offset-1 ring-offset-popover ring-primary" : ""}`}
                      />
                    ))}
                  </div>
                )}
              </div>
            ))}
            <Sep />
          </>
        )}
        {menu.href && (
          <>
            <Item icon={SquarePlus} onClick={run(() => openInNewTab(menu.href))}>
              Open in new ERP tab
            </Item>
            <Item icon={ExternalLink} onClick={run(() => window.open(menu.href, "_blank", "noopener"))}>
              Open in new browser tab
            </Item>
            <Item icon={Copy} onClick={run(() => navigator.clipboard?.writeText(window.location.origin + menu.href))}>
              Copy link
            </Item>
            <Sep />
          </>
        )}
        {menu.extHref && !menu.href && (
          <>
            <Item icon={ExternalLink} onClick={run(() => window.open(menu.extHref, "_blank", "noopener"))}>
              Open link in new browser tab
            </Item>
            <Sep />
          </>
        )}

        {/* Tabs */}
        <Item icon={RotateCw} onClick={run(() => reloadTab())} shortcut="F5">Reload tab</Item>
        {lockTarget && (
          <Item
            icon={isLocked ? LockOpen : Lock}
            onClick={run(() => toggleTabLock(lockTarget.id))}
          >
            {isLocked ? "Unlock tab" : "Lock tab"}
          </Item>
        )}
        {tabs.length > 1 && (
          <Item icon={X} onClick={run(() => closeOthers())}>Close other tabs</Item>
        )}
        <Sep />

        {/* Navigate */}
        <Item icon={ArrowLeft} onClick={run(() => window.history.back())}>Back</Item>
        <Item icon={ArrowRight} onClick={run(() => window.history.forward())}>Forward</Item>
        <Sep />

        {/* Page */}
        <Item icon={Printer} onClick={run(() => window.print(), 60)} shortcut="Ctrl+P">Print…</Item>
        <Item icon={Camera} onClick={run(screenshot, 90)}>Screenshot</Item>
        <Item icon={isFull ? Minimize2 : Maximize2} onClick={run(toggleFullscreen)} shortcut="F11">
          {isFull ? "Exit fullscreen" : "Fullscreen"}
        </Item>
        <Sep />

        <div className="flex items-center gap-2 px-2.5 py-1 text-[11px] text-muted-foreground">
          <MousePointerClick className="w-3 h-3 flex-shrink-0" />
          <span>Ctrl + right-click for the browser menu</span>
        </div>
      </div>

      {/* Filter flyout — a separate popup anchored beside the Filter row.
          Three modes (Clicked / Math / Compare) live in RowFilterFlyout. */}
      {menu.filter && subOpen && (() => {
        // The ref is still null on the render that OPENS the flyout (a header
        // right-click sets subOpen and mounts the button in the same pass), so
        // fall back to the Filter row's known geometry: it is always the first
        // row of the menu, inset by the menu's p-1 padding.
        const r =
          filterBtnRef.current?.getBoundingClientRect() ||
          { left, right: left + W, top: top + 4, bottom: top + 36 };
        const FW = 296;
        const FH = 470;
        const GAP = 10; // breathing room between the two panels
        const openRight = r.right + GAP + FW + 8 <= window.innerWidth;
        const fLeft = Math.round(openRight ? r.right + GAP : r.left - FW - GAP);
        const fTop = Math.round(Math.max(8, Math.min(r.top - 5, bottomLimit - FH)));
        return (
          <RowFilterFlyout
            filter={menu.filter}
            picks={getPicks()}
            active={active}
            style={{ position: "fixed", left: fLeft, top: fTop, width: FW, maxHeight: FH }}
            onMouseEnter={() => setSubOpen(true)}
            onDone={close}
          />
        );
      })()}
    </div>
  );

  return (
    <>
      {createPortal(panel, document.body)}
      {docDialog}
    </>
  );
};

export default ErpContextMenu;
